import React, { ReactNode } from 'react';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import { SAP_COLORS, SAP_TYPOGRAPHY } from '../../theme/sapDesignSystem';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  onClick?: () => void;
}

export interface PageLayoutProps {
  /** Page title (e.g. "Job Details") */
  title: string;
  /** Optional short description below the title */
  description?: string;
  /** Optional sx for title typography (e.g. color: SAP_COLORS.sapBlue) */
  titleSx?: object;
  /** Breadcrumb items; last item is typically current page (no path) */
  breadcrumbs?: BreadcrumbItem[];
  /** Actions rendered in the header (e.g. Export, Add) */
  actions?: ReactNode;
  /** Main content: filters, table, etc. */
  children: ReactNode;
}

/**
 * Standard page shell for list/detail screens.
 * Provides consistent title, description, breadcrumbs, actions, and content area.
 * Use with FilterBar and DrillDownTable for table-first pages.
 */
const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  description,
  titleSx,
  breadcrumbs,
  actions,
  children,
}) => {
  return (
    <Box
      sx={{
        p: 3,
        bgcolor: SAP_COLORS.sapGray1,
        minHeight: '100vh',
      }}
    >
      {/* Header: breadcrumbs + title/description + actions */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumbs
              separator="›"
              sx={{
                color: SAP_COLORS.sapGray5,
                mb: 1,
                '& .MuiBreadcrumbs-separator': { color: SAP_COLORS.sapGray4 },
              }}
            >
              {breadcrumbs.map((item, idx) => {
                const isLast = idx === breadcrumbs.length - 1;
                if (isLast || !item.path) {
                  return (
                    <Typography
                      key={idx}
                      component="span"
                      sx={{
                        color: SAP_COLORS.sapGray7,
                        fontSize: '0.875rem',
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                      }}
                    >
                      {item.label}
                    </Typography>
                  );
                }
                return (
                  <Link
                    key={idx}
                    component="button"
                    variant="body2"
                    onClick={item.onClick}
                    sx={{
                      color: SAP_COLORS.sapBlue,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                      '&:hover': { textDecoration: 'underline' },
                      border: 'none',
                      background: 'none',
                      padding: 0,
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )}
          <Typography
            variant="h4"
            sx={{
              color: SAP_COLORS.sapBlue,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
              mb: description ? 0.5 : 0,
              ...titleSx,
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body1"
              sx={{ color: SAP_COLORS.sapGray5, fontSize: '0.9375rem' }}
            >
              {description}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Box>

      {children}
    </Box>
  );
};

export default PageLayout;
